import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';

/**
 * Componente Modal reutilizable
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Indica si el modal está abierto
 * @param {function} props.onClose - Función para cerrar el modal
 * @param {string} props.title - Título del modal
 * @param {ReactNode} props.children - Contenido del modal
 * @param {string} props.size - Tamaño del modal ('sm', 'md', 'lg', 'xl', '2xl', '6xl', 'full')
 * @param {boolean} props.closeOnEsc - Si el modal se cierra al presionar Escape
 * @param {boolean} props.closeOnOverlayClick - Si el modal se cierra al hacer clic en el overlay
 * @param {boolean} props.showCloseButton - Si se muestra el botón de cerrar
 * @param {ReactNode} props.footer - Contenido personalizado para el pie del modal
 * @param {ReactNode} props.headerExtra - Contenido adicional para el header (lado derecho)
 * @returns {JSX.Element|null} - Componente Modal
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  closeOnEsc = true,
  closeOnOverlayClick = true,
  showCloseButton = true,
  footer = null,
  headerExtra = null
}) => {
  const modalRef = useRef(null);

  // Manejar cierre con tecla Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (isOpen && closeOnEsc && e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      // Bloquear scroll en el body
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      // Restaurar scroll
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, closeOnEsc, onClose]);

  // Cerrar al hacer clic en el overlay
  const handleOverlayClick = (e) => {
    // Solo cerrar si se hace clic directamente en el overlay (no en el contenido)
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  // Mapear tamaños a clases
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-4xl',
    '6xl': 'max-w-6xl',
    full: 'max-w-full'
  };
  const maxWidthClass = sizeClasses[size] || sizeClasses.md;

  // No renderizar nada si no está abierto
  if (!isOpen) return null;

  // Usar portal para renderizar fuera del árbol de componentes
  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1000] fade-in"
      onClick={handleOverlayClick}
      aria-modal="true"
      role="dialog"
    >
      <div 
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className={`bg-gray-800 text-white rounded-lg shadow-xl w-full ${maxWidthClass} modal-zoom-in overflow-hidden max-h-[95vh] sm:max-h-[90vh] flex flex-col modal-zoom-responsive`}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-700 bg-gray-800 sticky top-0 z-10">
          <div className="flex-1 min-w-0">
            {typeof title === 'string' ? (
              <h2 className="text-xl font-bold">{title}</h2>
            ) : (
              <div className="text-xl font-bold">{title}</div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {headerExtra && (
              <div className="flex-shrink-0">
                {headerExtra}
              </div>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white focus:outline-none p-1 rounded-full hover:bg-gray-700 transition-colors"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1">
          {children}
          
          {/* Footer integrado al final del contenido */}
          {footer && (
            <div className="mt-6 pt-4 border-t border-gray-700">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Agregamos los estilos CSS para la animación
const modalStyles = `
  @keyframes modalZoomInMobile {
    0% {
      transform: scale(0.9);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes modalZoomInDesktop {
    0% {
      transform: scale(0.8);
      opacity: 0;
    }
    100% {
      transform: scale(0.9);
      opacity: 1;
    }
  }

  .modal-zoom-in {
    animation: modalZoomInMobile 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  .modal-zoom-responsive {
    /* Mobile: sin zoom (escala 1) */
    transform: scale(1);
    transform-origin: center;
  }

  /* Desktop: con zoom 0.8 solo en pantallas md y superiores (768px+) */
  @media (min-width: 768px) {
    .modal-zoom-responsive {
      transform: scale(0.8);
    }
    
    .modal-zoom-in {
      animation: modalZoomInDesktop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }
  }
`;

// Inyectar estilos si no existen
if (typeof document !== 'undefined' && !document.getElementById('modal-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'modal-styles';
  styleSheet.textContent = modalStyles;
  document.head.appendChild(styleSheet);
}

export default Modal;